import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BadgeCheck, Download, Loader2, Lock, MapPin, Facebook, Instagram, Linkedin, Music2, Youtube, Globe, Phone, Mail } from 'lucide-react';
import QRCode from 'react-qr-code';
import api from '../../lib/api';
import { LOGO_DARK_BG } from '../../lib/constants';
import { useAuth } from '../../context/AuthContext';

export default function MemberCredential() {
  const { user } = useAuth();
  const [credential, setCredential] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    api
      .get('/me/credential')
      .then(({ data }) => setCredential(data))
      .catch((err) => {
        if (err.response?.status === 403) setLocked(true);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-cnib-blue" />
      </div>
    );
  }

  if (locked || !credential) {
    return (
      <div className="mx-auto flex max-w-lg flex-col items-center rounded-3xl border border-slate-200 bg-white px-8 py-16 text-center" data-testid="credential-locked">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-cnib-blue/10">
          <Lock className="h-8 w-8 text-cnib-blue" />
        </span>
        <h2 className="mt-6 font-heading text-2xl font-bold text-slate-900">Credencial disponible para socios</h2>
        <p className="mt-2 text-slate-600">
          Cuando tu solicitud de membresía sea aprobada, tu credencial digital con QR se activará aquí automáticamente.
        </p>
        <Link to="/hagase-socio" className="mt-6 rounded-full bg-orange-500 px-7 py-3 font-bold text-white transition-colors hover:bg-orange-600">
          Hágase socio
        </Link>
      </div>
    );
  }

  const publicUrl = `${window.location.origin}/p/${credential.credential_token}`;

  return (
    <div className="mx-auto max-w-lg space-y-6" data-testid="member-credential-page">
      <div className="text-center">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500">Credencial digital</p>
        <h1 className="mt-1 font-heading text-2xl font-extrabold text-slate-900 sm:text-3xl">Tu identidad CNIB</h1>
      </div>

      <div className="hero-grain relative overflow-hidden rounded-3xl bg-cnib-navy shadow-2xl" data-testid="member-qr-card">
        <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
          <img src={LOGO_DARK_BG} alt="CNIB" className="h-9 w-auto" />
          <span
            className={`rounded-full px-3 py-1 text-xs font-bold ${
              credential.status === 'active' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-amber-500/15 text-amber-400'
            }`}
          >
            {credential.status === 'active' ? 'Vigente' : 'Por verificar'}
          </span>
        </div>
        <div className="flex flex-col items-center px-6 py-8">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-orange-600 font-heading text-3xl font-extrabold text-white shadow-lg">
            {credential.picture ? (
              <img src={credential.picture} alt={credential.name} className="h-full w-full rounded-full object-cover" />
            ) : (
              credential.name?.charAt(0)
            )}
          </div>
          <h2 className="mt-4 text-center font-heading text-2xl font-bold text-white">{credential.name}</h2>
          <p className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-orange-400">
            <BadgeCheck className="h-4 w-4" /> {credential.category}
          </p>
          {credential.city && (
            <p className="mt-1 flex items-center gap-1 text-xs text-slate-400">
              <MapPin className="h-3.5 w-3.5" /> {credential.city}
            </p>
          )}
          <div className="mt-6 rounded-2xl bg-white p-4 shadow-xl">
            <QRCode value={publicUrl} size={160} fgColor="#0B1E3D" data-testid="member-qr-code" />
          </div>
          <p className="mt-4 font-mono text-sm font-bold tracking-wider text-slate-300">{credential.member_number}</p>
          {credential.valid_until && (
            <p className="mt-1 text-xs text-slate-500">
              Vigente hasta el{' '}
              {new Date(credential.valid_until).toLocaleDateString('es-BO', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          )}
          <CredentialSocials c={credential} />
        </div>
      </div>

      <button
        onClick={() => window.print()}
        data-testid="member-qr-credential-download"
        className="flex w-full items-center justify-center gap-2 rounded-full bg-cnib-blue py-3.5 font-bold text-white transition-colors hover:bg-cnib-bluehover"
      >
        <Download className="h-4 w-4" /> Descargar / imprimir credencial
      </button>
      <p className="text-center text-xs text-slate-500">
        Cualquier persona puede verificar tu vigencia escaneando el QR. No comparte datos privados.
      </p>
    </div>
  );
}

function CredentialSocials({ c }) {
  const items = [
    c.whatsapp && { icon: Phone, href: `https://wa.me/${c.whatsapp.replace(/[^0-9]/g, '')}`, label: 'WhatsApp' },
    c.email && { icon: Mail, href: `mailto:${c.email}`, label: 'Email' },
    c.social_instagram && { icon: Instagram, href: c.social_instagram, label: 'Instagram' },
    c.social_facebook && { icon: Facebook, href: c.social_facebook, label: 'Facebook' },
    c.social_linkedin && { icon: Linkedin, href: c.social_linkedin, label: 'LinkedIn' },
    c.social_tiktok && { icon: Music2, href: c.social_tiktok, label: 'TikTok' },
    c.social_youtube && { icon: Youtube, href: c.social_youtube, label: 'YouTube' },
    c.social_website && { icon: Globe, href: c.social_website, label: 'Sitio web' },
  ].filter(Boolean);
  if (items.length === 0) return null;
  return (
    <div className="mt-5 flex flex-wrap items-center justify-center gap-2" data-testid="credential-socials">
      {items.map((it, i) => (
        <a
          key={i}
          href={it.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={it.label}
          title={it.label}
          className="flex h-8 w-8 items-center justify-center rounded-full border border-white/15 bg-white/5 text-slate-200 transition-all hover:border-orange-400 hover:bg-orange-500/20 hover:text-orange-300"
        >
          <it.icon className="h-3.5 w-3.5" />
        </a>
      ))}
    </div>
  );
}
